import { useState } from 'react';
import { SchoolAvatar } from './SchoolAvatar';

export interface PledgeTarget {
  id: string;
  name: string;
  conferenceId: string;
  division: string;
  primary: string;
  secondary: string;
}

interface Props {
  target: PledgeTarget;
  conferenceName: (id: string) => string;
  onSubmit: (schoolId: string, amountCents: number, handle?: string) => Promise<void>;
  onCancel: () => void;
}

/** Amount + optional handle + "Add points" — shared by search and board-row entry. */
export function PledgePanel({ target, conferenceName, onSubmit, onCancel }: Props) {
  const [dollars, setDollars] = useState('5');
  const [handle, setHandle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const cents = Math.round(Number(dollars) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setError('Enter a dollar amount greater than 0.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSubmit(target.id, cents, handle.trim() || undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add points');
      setBusy(false);
    }
  }

  return (
    <div className="pledge">
      <div className="pledge__school">
        <SchoolAvatar name={target.name} primary={target.primary} secondary={target.secondary} size={36} />
        <span className="pledge__name">{target.name}</span>
        <span className="chip">{conferenceName(target.conferenceId)}</span>
        <span className="chip chip--div">{target.division}</span>
      </div>
      <div className="pledge__row">
        <span className="add-inline__dollar">$</span>
        <input
          aria-label="Amount"
          type="number"
          min={1}
          step={1}
          value={dollars}
          onChange={(e) => setDollars(e.target.value)}
        />
        <input
          aria-label="Handle"
          placeholder="@you (optional)"
          maxLength={40}
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
        />
        <button className="btn btn--primary" onClick={submit} disabled={busy}>
          {busy ? 'Redirecting…' : 'Add points'}
        </button>
        <button className="btn btn--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
